import { useMemo, useSyncExternalStore } from "react";
import type { AspectRatio } from "@shared/aspectRatios";
import {
  plannedSizeRequests, sizeDownloadFilename, MAX_SIZE_DOWNLOAD_ENTRIES,
  type SizeDownloadItem, type SizeOperationView,
} from "@shared/iteration-size-downloads";

export type IterationSizeController = {
  get: (adId: string, ratio: AspectRatio) => SizeOperationView | undefined;
  run: (setId: string, items: SizeDownloadItem[], ratios: AspectRatio[], retry?: boolean) => Promise<void>;
  download: (setId: string, items: SizeDownloadItem[], ratios: AspectRatio[], batch: boolean, failedOnly?: boolean) => Promise<void>;
  checkStatus: (setId: string, items: SizeDownloadItem[], ratios: AspectRatio[]) => Promise<void>;
  save: (setId: string, items: SizeDownloadItem[], ratios: AspectRatio[]) => Promise<number>;
  getSaved: (adId: string, ratio: AspectRatio) => string | undefined;
  saveFailures: Array<{ name: string; message: string }>;
  busy: boolean;
  packagingFailures: Array<{ name: string; message: string }>;
};
type Entry = SizeOperationView & { setId: string; adId: string; ratio: AspectRatio; savedAdId?: string };
const entryKey = (adId: string, ratio: AspectRatio) => `${adId}:${ratio}`;
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * One queue shared by every tile and set on the page. Locks are acquired
 * synchronously, not through React state. Closing a popover never cancels it.
 */
export class IterationSizeQueue {
  private static pageActive = 0;
  private static waiting = new Set<IterationSizeQueue>();
  private entries = new Map<string, Entry>();
  private inFlight = new Map<string, Promise<void>>();
  private jobs: Array<() => Promise<void>> = [];
  private active = 0;
  private packaging = false;
  private saving = false;
  private packagingFailedTokens = new Set<string>();
  private listeners = new Set<() => void>();
  revision = 0;
  packagingFailures: Array<{ name: string; message: string }> = [];
  saveFailures: Array<{ name: string; message: string }> = [];
  constructor(
    private readonly scope: string,
    private readonly transport: typeof fetch = (...args) => fetch(...args),
    private readonly wait = sleep,
    private readonly storage: Pick<Storage, "getItem" | "setItem"> | undefined = typeof sessionStorage === "undefined" ? undefined : sessionStorage,
  ) {
    try {
      const saved = JSON.parse(storage?.getItem(`iteration-size-downloads:${scope}`) || "[]");
      if (Array.isArray(saved)) for (const entry of saved) {
        if (typeof entry?.adId !== "string" || !entry?.token) continue;
        // A saved pending ticket can be resumed safely; running/uncertain
        // tickets are polled, never submitted as fresh paid operations.
        this.entries.set(entryKey(entry.adId, entry.ratio), entry);
      }
    } catch { /* Unavailable session storage does not alter the live queue. */ }
  }
  get busy() { return this.active > 0 || this.jobs.length > 0 || this.packaging || this.saving; }
  get = (adId: string, ratio: AspectRatio) => this.entries.get(entryKey(adId, ratio));
  getSaved = (adId: string, ratio: AspectRatio) => this.get(adId, ratio)?.savedAdId;
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  snapshot = () => this.revision;
  private changed() {
    this.revision++;
    try { this.storage?.setItem(`iteration-size-downloads:${this.scope}`, JSON.stringify([...this.entries.values()])); } catch { /* Live locks remain authoritative. */ }
    for (const listener of this.listeners) listener();
  }
  private pump() {
    if (this.jobs.length) IterationSizeQueue.waiting.add(this);
    while (IterationSizeQueue.pageActive < 3 && this.active < 3 && this.jobs.length) {
      IterationSizeQueue.pageActive++;
      this.active++;
      const job = this.jobs.shift()!;
      void job().finally(() => {
        IterationSizeQueue.pageActive--;
        this.active--; this.changed();
        for (const queue of IterationSizeQueue.waiting) queue.pump();
      });
    }
    if (!this.jobs.length) IterationSizeQueue.waiting.delete(this);
  }
  private async read(response: Response) {
    const json = await response.json();
    if (!response.ok && !json?.status) throw Object.assign(new Error(json?.message || `Size operation failed (${response.status})`), {
      definitive: response.status >= 400 && response.status < 500 && response.status !== 408,
    });
    return json as SizeOperationView;
  }
  private async status(entry: Entry) {
    const response = await this.transport(`/api/creative-iterations/sets/${entry.setId}/size-downloads/operations/${entry.token}`, { credentials: "include" });
    const next = await this.read(response);
    Object.assign(entry, next);
    this.changed();
  }
  private async process(setId: string, item: SizeDownloadItem, ratio: AspectRatio, retry: boolean) {
    const key = entryKey(item.adId, ratio);
    let entry = this.entries.get(key);
    let renderAttempted = false;
    try {
      if (!entry?.token || retry && entry.status === "failed") {
        const response = await this.transport(`/api/creative-iterations/sets/${setId}/size-downloads/operations`, {
          method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ adId: item.adId, aspectRatio: ratio, retry }),
        });
        entry = { ...await this.read(response), setId, adId: item.adId, ratio };
        this.entries.set(key, entry);
        this.changed(); // Persist the ticket BEFORE any paid request.
      }
      if (entry.status === "done" || entry.status === "failed") return;
      if (entry.status === "pending") {
        // Both admission and render use the same single-use server ticket.
        renderAttempted = true;
        entry.status = "running"; this.changed();
        try {
          const response = await this.transport(`/api/generated-ads/${item.adId}/reframe`, {
            method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
            signal: AbortSignal.timeout(180_000),
            body: JSON.stringify({ aspectRatio: ratio, iterationSizeDownload: { setId, token: entry.token } }),
          });
          if (response.status === 429) {
            // Ticket is unconsumed, but do not race other page activity.
            await this.status(entry);
            if (this.get(item.adId, ratio)?.status === "pending") {
              entry.status = "failed"; // Capacity rejection did not consume the ticket.
              entry.message = "Three renders are active. Resume this prepared size after they finish.";
              return;
            }
          } else Object.assign(entry, await this.read(response));
          this.changed();
        } catch (error: any) {
          // Lost HTTP response is not evidence the render failed.
          entry.status = error?.definitive ? "failed" : "uncertain"; this.changed();
        }
      }
      const deadline = Date.now() + 10 * 60_000;
      while (entry.status === "running" || entry.status === "uncertain") {
        if (Date.now() > deadline) throw new Error("Render status is still unknown. Check status later; do not rerender.");
        await this.wait(2_000);
        await this.status(entry);
        if (entry.status === "uncertain") break; // Server classified an ambiguous provider outcome.
      }
    } catch (error) {
      const uncertain = renderAttempted || !!entry?.token;
      const message = error instanceof Error ? error.message : "Operation failed";
      entry = { ...(entry || { token: "", setId, adId: item.adId, ratio }), status: uncertain ? "uncertain" : "failed", message };
      this.entries.set(key, entry);
    } finally { this.changed(); }
  }
  run = async (setId: string, items: SizeDownloadItem[], ratios: AspectRatio[], retry = false) => {
    const tasks = plannedSizeRequests(items, ratios);
    if (tasks.length > MAX_SIZE_DOWNLOAD_ENTRIES) throw new Error("Choose no more than 32 ad/size combinations");
    const promises = tasks.map(({ item, ratio }) => {
      const key = entryKey(item.adId, ratio);
      const existing = this.inFlight.get(key);
      if (existing) return existing;
      const entry = this.entries.get(key);
      if (entry?.status === "done" || entry?.status === "failed" && !retry) return Promise.resolve();
      let resolve!: () => void;
      const promise = new Promise<void>(done => { resolve = done; });
      this.inFlight.set(key, promise);
      if (!entry) this.entries.set(key, { token: "", status: "pending", setId, adId: item.adId, ratio });
      this.jobs.push(async () => {
        try { await this.process(setId, item, ratio, retry); }
        finally { this.inFlight.delete(key); resolve(); }
      });
      return promise;
    });
    this.changed(); this.pump();
    await Promise.all(promises);
  };
  checkStatus = async (setId: string, items: SizeDownloadItem[], ratios: AspectRatio[]) => {
    for (const { item, ratio } of plannedSizeRequests(items, ratios)) {
      const entry = this.entries.get(entryKey(item.adId, ratio));
      if (!entry?.token || entry.setId !== setId || entry.status !== "uncertain" && entry.status !== "running") continue;
      try { await this.status(entry); }
      catch (error) {
        entry.message = error instanceof Error ? error.message : "Status unavailable";
        this.changed();
      }
    }
  };
  save = async (setId: string, items: SizeDownloadItem[], ratios: AspectRatio[]) => {
    if (this.saving) return 0;
    const requested = plannedSizeRequests(items, ratios);
    const successes = requested.filter(({ item, ratio }) => this.get(item.adId, ratio)?.status === "done");
    if (!successes.length) throw new Error("No completed sizes to save. Check failed or uncertain renders.");
    const tokens = [...new Set(successes.map(({ item, ratio }) => this.get(item.adId, ratio)!.token))];
    this.saving = true; this.saveFailures = []; this.changed();
    try {
      const response = await this.transport(`/api/creative-iterations/sets/${setId}/size-downloads/save`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        signal: AbortSignal.timeout(180_000), body: JSON.stringify({ tokens }),
      });
      const data = await response.json();
      this.saveFailures = data.failures || [];
      if (!response.ok) throw new Error(data.message || "No sizes were saved. Retry saving without rerendering.");
      for (const saved of data.saved || []) {
        const entry = [...this.entries.values()].find(value => value.token === saved.token);
        if (entry) entry.savedAdId = saved.generatedAdId;
      }
      return data.saved?.length || 0;
    } catch (error) {
      if (!this.saveFailures.length) this.saveFailures = successes.map(({ item, ratio }) => ({
        name: `${item.name} (${ratio})`, message: "Save interrupted. Retry saving; successful renders will be reused.",
      }));
      throw error;
    } finally { this.saving = false; this.changed(); }
  };
  download = async (setId: string, items: SizeDownloadItem[], ratios: AspectRatio[], batch: boolean, failedOnly = false) => {
    if (this.packaging) return;
    const requested = plannedSizeRequests(items, ratios);
    const successes = requested.filter(({ item, ratio }) => {
      const entry = this.get(item.adId, ratio);
      return entry?.status === "done" && (!failedOnly || this.packagingFailedTokens.has(entry.token));
    });
    if (!successes.length) throw new Error("No successful sizes to download. Review failed or uncertain sizes.");
    this.packaging = true; this.packagingFailures = []; this.changed();
    const tokens = [...new Set(successes.map(({ item, ratio }) => this.get(item.adId, ratio)!.token))];
    try {
      const response = await this.transport(`/api/creative-iterations/sets/${setId}/size-downloads/package`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        signal: AbortSignal.timeout(150_000),
        body: JSON.stringify({ tokens, batch: batch || requested.length > 1 }),
      });
      if (!response.ok) {
        const body = await response.json();
        this.packagingFailures = body.failures || [];
        this.packagingFailedTokens = new Set(body.failures?.map((failure: { token: string }) => failure.token) || tokens);
        throw new Error(body.message || "Packaging failed. Download again without rerendering.");
      }
      const failed = JSON.parse(response.headers.get("X-Size-Download-Failures") || "[]");
      this.packagingFailedTokens = new Set(failed.map((failure: { token: string }) => failure.token));
      this.packagingFailures = failed.map((failure: { token: string }) => {
        const match = successes.find(({ item, ratio }) => this.get(item.adId, ratio)?.token === failure.token);
        return { name: match ? `${match.item.name} (${match.ratio})` : "Size output", message: "Could not package this PNG. Retry download without rerendering; see download-failures.json for details." };
      });
      const blob = await response.blob();
      if (!blob.size) throw new Error("Empty download received. Retry packaging, not generation.");
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      const disposition = response.headers.get("Content-Disposition");
      anchor.download = disposition?.match(/filename="([^"]+)"/)?.[1]
        || (batch || requested.length > 1 ? `creative-sizes.zip` : sizeDownloadFilename(successes[0].item.name, successes[0].ratio, false));
      document.body.appendChild(anchor); anchor.click(); anchor.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
    } catch (error) {
      if (!this.packagingFailures.length) {
        this.packagingFailedTokens = new Set(tokens);
        this.packagingFailures = successes.map(({ item, ratio }) => ({
          name: `${item.name} (${ratio})`,
          message: "Download interrupted. Retry these files without rendering again.",
        }));
      }
      throw error;
    } finally { this.packaging = false; this.changed(); }
  };
}

const pageQueues = new Map<string, IterationSizeQueue>();
export function useIterationSizeDownloads(enabled: boolean, scope: string): IterationSizeController {
  const queue = useMemo(() => {
    let result = pageQueues.get(scope);
    if (!result) { result = new IterationSizeQueue(scope); pageQueues.set(scope, result); }
    return result;
  }, [scope]);
  useSyncExternalStore(queue.subscribe, queue.snapshot, queue.snapshot);
  return {
    get: queue.get, busy: queue.busy, packagingFailures: queue.packagingFailures,
    getSaved: queue.getSaved, saveFailures: queue.saveFailures,
    save: async (...args) => { if (!enabled) throw new Error("Size saving is not enabled"); return queue.save(...args); },
    run: async (...args) => { if (!enabled) throw new Error("Size downloads are not enabled"); await queue.run(...args); },
    download: async (...args) => { if (!enabled) throw new Error("Size downloads are not enabled"); await queue.download(...args); },
    checkStatus: async (...args) => { if (!enabled) throw new Error("Size downloads are not enabled"); await queue.checkStatus(...args); },
  };
}
